import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { IconButton, TextField, Tooltip, Chip } from "@mui/material";
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Person as UserIcon,
  Assistant as AIIcon,
  RestartAlt,
  Assistant
} from "@mui/icons-material";
import { CHAT_URL } from "../config/config";

const AppChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("none");

  const selectedApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  );

  const [chatHistory, setChatHistory] = useState([
    {
      type: "answer",
      content: "first message"
    }
  ]);

  useEffect(() => {
    if (selectedApplicationData) {
      const applicantName =
        selectedApplicationData?.applicant_info?.name ||
        selectedApplicationData?.customer_name ||
        "Unknown Applicant";

      setChatHistory([
        {
          type: "answer",
          content: `Hi! How can I assist you with the applicant <strong>${applicantName} (${selectedApplicationData?.application_id})</strong>?`
        }
      ]);
    }
  }, [selectedApplicationData]);

  const chatContainerRef = useRef(null);

  const toggleChatBot = () => {
    setIsOpen(!isOpen);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const resetChat = () => {
    setSessionId("none");
    setMessage("");
    setLoading(false);

    const applicantName =
      selectedApplicationData?.applicant_info?.name ||
      selectedApplicationData?.customer_name ||
      "Unknown Applicant";

    setChatHistory([
      {
        type: "answer",
        content: `Hi! How can I assist you with the applicant <strong>${applicantName} (${selectedApplicationData?.application_id})</strong>?`
      }
    ]);
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleSendMessage = async () => {
    if (!message.trim() || loading) return;

    const newChatHistory = [
      ...chatHistory,
      { type: "question", content: message },
      { type: "answer", content: "loading" }
    ];

    setChatHistory(newChatHistory);
    setLoading(true);
    setMessage("");

    try {
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");
      const headers = {
        "X-Access-Token": accessToken,
        "X-Refresh-Token": refreshToken,
        Accept: "application/json"
      };

      const body = {
        session_id: sessionId,
        application_id: selectedApplicationData["application_id"],
        query: message
      };

      const response = await axios.post(`${CHAT_URL}/bot/chat`, body, {
        headers
      });

      const { answer, references } = response?.data || {};
      let botResponse = answer || "No response from the server.";

      if (Array.isArray(references) && references.length > 0) {
        const referenceLinks = references
          .map(
            (ref, i) =>
              `<div style="margin-top: 6px; font-size: 13px;">
                <strong>Ref ${i + 1}:</strong>
                <a href="${
                  ref.uri
                }" target="_blank" style="color: #1d4ed8; text-decoration: underline;">
                  ${ref.file_name || "View Document"}
                </a>
              </div>`
          )
          .join("");
        botResponse += `<div style="margin-top: 10px;">${referenceLinks}</div>`;
      }

      const newSessionId = response?.data?.session_id || sessionId;

      setChatHistory(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          type: "answer",
          content: botResponse
        };
        return updated;
      });

      setSessionId(newSessionId);
    } catch (err) {
      setChatHistory(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          type: "error",
          content: "Error fetching response."
        };
        return updated;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = event => {
    if (event.key === "Enter") handleSendMessage();
  };

  return (
    <div
      className="fixed bottom-4 bg-orange-600 rounded-lg chat-bot overflow-hidden"
      style={{
        right: "1%",
        border: `2px solid ${isOpen ? "#ea580c" : "transparent"}`,
        width: isOpen ? "35%" : undefined
      }}
    >
      {!isOpen && (
        <IconButton
          onClick={toggleChatBot}
          className="bg-orange-600 text-white p-3 rounded-full shadow-lg"
          style={{ color: "white" }}
        >
          <Assistant />
        </IconButton>
      )}

      {isOpen && (
        <div className="w-[100%] h-[32rem] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500 to-orange-700 text-white p-3 flex justify-between items-center">
            <div className="flex items-center">
              <AIIcon className="mr-2" />
              <h2 className="text-lg font-bold">Ask LexAI</h2>
            </div>
            <div className="flex space-x-2">
              <Tooltip title="Reset Conversation">
                <IconButton onClick={resetChat}>
                  <RestartAlt className="text-white" />
                </IconButton>
              </Tooltip>
              <IconButton onClick={toggleChatBot}>
                <CloseIcon className="text-white" />
              </IconButton>
            </div>
          </div>

          <div
            className="flex-1 p-4 overflow-y-auto space-y-4 bg-neutral-50"
            ref={chatContainerRef}
          >
            {chatHistory.map((item, index) => (
              <div
                key={index}
                className={`flex items-start ${
                  item.type === "question"
                    ? "justify-end pl-5"
                    : "justify-start pr-5"
                }`}
              >
                {item.type === "answer" && (
                  <div className="flex flex-col items-center mr-2">
                    <AIIcon className="text-orange-600" fontSize="small" />
                  </div>
                )}

                {item.content === "loading" ? (
                  <div className="flex space-x-1">
                    <span className="w-2 h-2 bg-orange-600 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-orange-600 rounded-full animate-bounce animation-delay-200"></span>
                    <span className="w-2 h-2 bg-orange-600 rounded-full animate-bounce animation-delay-400"></span>
                  </div>
                ) : (
                  <p
                    className={`p-3 rounded-lg max-w-[85%] ${
                      item.type === "question"
                        ? "bg-orange-200 text-gray-800 border border-gray-200 shadow-md"
                        : item.type === "error"
                        ? "bg-red-50 text-red-700 border border-red-100"
                        : "bg-white text-gray-700 border border-gray-100 shadow-md"
                    }`}
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  ></p>
                )}
                {item.type === "question" && (
                  <UserIcon className="ml-2 text-orange-700" />
                )}
              </div>
            ))}
          </div>

          <div className="p-3 border-t bg-white flex items-center space-x-2">
            <IconButton
              color="primary"
              component="label"
              sx={{ color: "#ea580c" }}
            >
              <AttachFileIcon />
              <input type="file" hidden />
            </IconButton>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#ea580c"
                  }
                }
              }}
            />
            <IconButton
              sx={{ color: "#ea580c" }}
              onClick={handleSendMessage}
              disabled={loading}
            >
              <SendIcon />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppChatBot;
